import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { MdClose, MdNotifications, MdVolumeOff } from "react-icons/md";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes.js";
import { useAuth } from "contexts/AuthContext";
import {
  startNewOrderSound,
  stopNewOrderSound,
  unlockNewOrderSound,
} from "hooks/useNewOrderAlert";
import { subscribeToRestaurantOrders } from "utils/pusher";

export default function Admin(props) {
  const { ...rest } = props;
  const { restaurantId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(true);
  const [currentRoute, setCurrentRoute] = React.useState("Main Dashboard");
  const [newOrder, setNewOrder] = React.useState(null);
  const [audioBlocked, setAudioBlocked] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () =>
      window.innerWidth < 1200 ? setOpen(false) : setOpen(true);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  React.useEffect(() => {
    const unlockAudio = () => {
      unlockNewOrderSound();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };

    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);
  React.useEffect(() => {
    getActiveRoute(routes);
  }, [location.pathname]);
  React.useEffect(() => {
    if (!restaurantId) {
      console.warn("[Pusher] restaurantId absent, abonnement ignore");
      return undefined;
    }

    return subscribeToRestaurantOrders(restaurantId, (data) => {
      if (Number(data?.restaurant_id) !== Number(restaurantId)) {
        console.warn("[Pusher] evenement ignore pour un autre restaurant", {
          expected: restaurantId,
          received: data?.restaurant_id,
        });
        return;
      }

      console.log("[Pusher] nouvelle commande partenaire traitee", data);
      setNewOrder(data);
      setAudioBlocked(false);
      startNewOrderSound().then((played) => {
        if (played === false) setAudioBlocked(true);
      });
      window.dispatchEvent(new CustomEvent("restaurant:new-order", { detail: data }));
    });
  }, [restaurantId]);

  const dismissNewOrder = () => {
    stopNewOrderSound();
    setNewOrder(null);
  };

  const openNewOrder = () => {
    stopNewOrderSound();
    setNewOrder(null);
    navigate("/admin/orders");
  };

  const enableSound = () => {
    setAudioBlocked(false);
    startNewOrderSound().then((played) => {
      if (played === false) setAudioBlocked(true);
    });
  };

  const getActiveRoute = (routes) => {
    let activeRoute = "Main Dashboard";
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(
          routes[i].layout + "/" + routes[i].path
        ) !== -1
      ) {
        setCurrentRoute(routes[i].name);
      }
    }
    return activeRoute;
  };
  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].secondary;
      }
    }
    return activeNavbar;
  };
  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/admin") {
        return (
          <Route path={`/${prop.path}`} element={prop.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };

  document.documentElement.dir = "ltr";
  return (
    <div className="flex h-full w-full">
      {newOrder && (
        <div className="fixed right-5 top-5 z-[100] w-[min(92vw,420px)] rounded-2xl border border-blue-200 bg-white p-4 shadow-2xl dark:border-blue-900 dark:bg-navy-800">
          <button
            type="button"
            aria-label="Fermer"
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-700 dark:hover:text-white"
            onClick={dismissNewOrder}
          >
            <MdClose className="h-5 w-5" />
          </button>

          <div className="flex gap-3 pr-6">
            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <MdNotifications className="h-6 w-6" />
            </span>
            <div>
              <p className="font-bold text-navy-700 dark:text-white">
                Nouvelle commande
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Commande #{newOrder.ref || newOrder.reference || "Référence indisponible"}
              </p>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            {audioBlocked && (
              <button
                type="button"
                className="rounded-xl bg-orange-500 px-3 py-2 text-sm font-bold text-white hover:bg-orange-600"
                onClick={enableSound}
              >
                Activer le son
              </button>
            )}
            <button
              type="button"
              className="flex-1 rounded-xl bg-brand-500 px-4 py-2 text-sm font-bold text-white hover:bg-brand-600"
              onClick={openNewOrder}
            >
              Voir les commandes
            </button>
            <button
              type="button"
              className="flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600 dark:border-navy-600 dark:text-gray-200"
              onClick={dismissNewOrder}
            >
              <MdVolumeOff className="h-4 w-4" />
              Couper
            </button>
          </div>
        </div>
      )}
      <Sidebar open={open} onClose={() => setOpen(false)} />
      {/* Navbar & Main Content */}
      <div className="h-full w-full bg-lightPrimary dark:!bg-navy-900">
        {/* Main Content */}
        <main
          className={`mx-[12px] h-full flex-none transition-all md:pr-2 xl:ml-[313px]`}
        >
          {/* Routes */}
          <div className="h-full">
            <Navbar
              onOpenSidenav={() => setOpen(true)}
              logoText={"Horizon UI Tailwind React"}
              brandText={currentRoute}
              secondary={getActiveNavbar(routes)}
              {...rest}
            />
            <div className="pt-5s mx-auto mb-auto h-full min-h-[84vh] p-2 md:pr-2">
              <Routes>
                {getRoutes(routes)}

                <Route
                  path="/"
                  element={<Navigate to="/admin/orders" replace />}
                />
              </Routes>
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
